import AccordionCustom from "@components/Accordion/AccordionCustom";
import ImageGrid from "@components/ImageGrid";
import CardDataCarousal from "@components/LandingPageComponents/CardDataCarousal";
import { CardDataDetailsProps } from "@components/LandingPageComponents/CardDataDetails";
import FeatureCardSection from "@components/LandingPageComponents/CardSection";
import FaqHeader from "@components/LandingPageComponents/RazorPayComponents/FaqHeader";
import ShopifyHeader from "@components/LandingPageComponents/shopifyHeader";
import TestimonialSection from "@components/LandingPageComponents/TestimonialSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import NewLayout from "@components/new-ui/Layout";
import YellowCTAhref from "@components/yellowCTAhref";
import Head from "next/head";
import React from "react";

const cardDataCollection: CardDataDetailsProps[] = [
    {
        img: "https://assets.gallabox.com/gb-home/shopify-landing/reduce-cart.png",
        title: "Reduce cart abandonment with WhatsApp reminders",
        description: "Reduce cart abandonment and never let opportunities slip through the cracks by sending automated reminders on WhatsApp by offering discounts.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/shopify-landing/sync-shopify.png",
        title: "Sync Shopify products on WhatsApp store",
        description: "Automatically sync your Shopify product catalogs to your WhatsApp store and boost your sales by running personalized campaigns at scale.",
    },

    {
        img: "https://assets.gallabox.com/gb-home/shopify-landing/icon3.png",
        title: "Convert WhatsApp carts to Shopify carts",
        description:
            "Offer a seamless shopping experience by enabling customers to place orders on your Shopify store from within the WhatsApp interface, enabling faster checkouts.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/hubspot-track-customer.svg",
        title: "Automate order updates & track engagement",
        description:
            "Automate notification flows for all orders, including CoD, track abandoned carts, delivery status, message sends, opens, and clicks for all Shopify orders on WhatsApp.",
    },
];

const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "What is WhatsApp Shopify Integration?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "The WhatsApp Shopify integration powered by Gallabox connects your Shopify store with WhatsApp Business API so that you can engage your Shopify customers better using WhatsApp.",
            },
        },
        {
            "@type": "Question",
            name: "What type of campaigns can I run with WhatsApp marketing automation?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "By integrating WhatsApp and Shopify workflow, you can run various WhatsApp marketing automation campaigns for cart abandonment, follow-ups, payment notifications, and more, enhancing customer engagement and revenue.",
            },
        },
        {
            "@type": "Question",
            name: "What are the benefits of having WhatsApp for Shopify?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Connecting your WhatsApp and Shopify account helps you to send product catalogs, notifications and messages to your customers on WhatsApp and enable them to shop without having to exit the WhatsApp interface.",
            },
        },
        {
            "@type": "Question",
            name: "What types of messages can I send through the Shopify WhatsApp automation?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "You can use them to reduce cart abandonment, notify order confirmations, shipment updates, product updates. You can run promotions, announce new products and services, share images of your products, or offer better customer service.",
            },
        },
        {
            "@type": "Question",
            name: "How does the abandoned cart with WhatsApp reminders work?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "When a customer adds items to their shopping cart on an online store but leaves without completing the purchase, Gallabox triggers an automated WhatsApp reminder.",
            },
        },
        {
            "@type": "Question",
            name: "How do I set up the Abandoned Cart with WhatsApp Reminders on Gallabox?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Install Gallabox from Shopify Marketplace. Under the workflows enable cart abandonment. Choose the WhatsApp template you want to trigger.  Tadaa you have enabled the cart abandonment!",
            },
        },
    ],
};

const shopifyLanding: React.FC = () => {
    return (
        <NewLayout
            title="Shopify WhatsApp Integration for Sales, Marketing & Support"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/shopify-landing/shopify-page-og.png" }}
            canonical="https://gallabox.com/whatsapp-integrations/shopify"
            description="Increase sales with Gallabox’s Shopify WhatsApp integration. Send order updates, recover carts, and engage customers easily."
        >
            <Head>
                <script type="application/ld+json">{JSON.stringify(faqPageSchema)}</script>
            </Head>
            <ShopifyHeader subtitle="Whatsapp Shopify Integration" title="Shopify WhatsApp Integration for Better Customer Support" />

            <div className="lg:max-w-7xl mx-auto">
                <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                <ClientLogos />
            </div>
            <CardDataCarousal cardDataCollection={cardDataCollection} />
            <FeatureCardSection
                subTitle="Order Updates"
                title="Send order updates through WhatsApp Notifications"
                imageURl="https://assets.gallabox.com/gb-home/shopify-landing/shopify-image6.png"
                description="Stay connected with your Shopify customers on WhatsApp and provide real-time updates on their orders and shipping status. Provide a convenient ordering experience and address their queries 24x7 using smart no-code WhatsApp chatbots."
                isImageRightSide
            />
            <FeatureCardSection
                subTitle="Abandoned cart triggers"
                imageURl="https://assets.gallabox.com/gb-home/shopify-landing/shopify-image2.png"
                title="Recover up to 60% of abandoned carts. Prevent revenue leaks"
                description="Win back the prospects lost to abandoned carts on your Shopify store with automated WhatsApp reminders and personalized messages. Offer custom discounts nudging them to take action and prevent revenue leaks."
            />
            <FeatureCardSection
                imageURl="https://assets.gallabox.com/gb-home/shopify-landing/shopify-image3.png"
                subTitle="Order Feedback"
                title="Follow up and engage with your customers post purchase"
                description="Leverage Gallabox's drip marketing capabilities to automate feedback gathering from customers after order delivery.  Gather valuable customer insights and use them to improve customer satisfaction and loyalty through proactive engagement."
                isImageRightSide
            />
            <FeatureCardSection
                imageURl="https://assets.gallabox.com/gb-home/shopify-landing/shopify-image4.png"
                subTitle="WhatsApp Broadcast"
                title="Run personalized broadcasts to customers based on events."
                description="Classify customers based on their buying behavior on your Shopify store, such as 'bought in the last 30 days,' and run targeted broadcast campaigns. Enhance your marketing precision, reaching the right audience with the right message, leading to better engagement and increased ROI"
            />
            <FeatureCardSection
                imageURl="https://assets.gallabox.com/gb-home/shopify-landing/shopify-image5.png"
                subTitle="WhatsApp Prepaid Automation"
                title="Convert COD orders to prepaid"
                description="Send automate personalized messages to motivate your customers who choose Cash on Delivery (COD) to prepaid payment option. Using Gallabox you can automate and personalize communication, to build stronger connections with your customers, improve their overall engagement and experience, and improve sales with smart automations"
                isImageRightSide
            />
            <YellowCTAhref customText="Connect Gallabox and Shopify in minutes, no code required" />
            <TestimonialSection
                TestimonialSectionPropsData={[
                    {
                        companyImg: "https://assets.gallabox.com/gb-home/travelmite-logo.svg",
                        avatarImg: "https://assets.gallabox.com/gb-home/shopify-landing/companyowner1.png",
                        headerTitle: "We used to send 5-6 SMS for every order, now we use Gallabox to send the notifications automatically",
                        nameText: "Praveen",
                        professionText: "VP of Sales, Travelmite",
                    },
                    {
                        companyImg: "https://assets.gallabox.com/gb-home/g-sheet/googlesheet-comp1.png",
                        avatarImg: "https://assets.gallabox.com/gb-home/g-sheet/googlesheet-owner1.png",
                        headerTitle: "Even though I’m not a tech expert, I was able to set up the integration without any hassle.",
                        nameText: "Harish",
                        professionText: "Founder, E Panipuri Cart",
                    },
                ]}
            />
            <ImageGrid />
            <YellowCTAhref customText="Engage customers on your Shopify store using WhatsApp" buttonText="Get started Today" />
            <FaqHeader description="Everything you need to know about the Shopify-Gallabox integration" />
            <AccordionCustom
                title="Shopify Integration"
                items={[
                    {
                        question: "What is WhatsApp Shopify Integration?",
                        answer: "The WhatsApp Shopify integration powered by Gallabox connects your Shopify store with WhatsApp Business API so that you can engage your Shopify customers better using WhatsApp.",
                    },
                    {
                        question: "What type of campaigns can I run with WhatsApp marketing automation?",
                        answer: "By integrating WhatsApp and Shopify workflow, you can run various WhatsApp marketing automation campaigns for cart abandonment, follow-ups, payment notifications, and more, enhancing customer engagement and revenue.",
                    },
                    {
                        question: "What are the benefits of having WhatsApp for Shopify?",
                        answer: "Connecting your WhatsApp and Shopify account helps you to send product catalogs, notifications and messages to your customers on WhatsApp and enable them to shop without having to exit the WhatsApp interface.",
                    },
                    {
                        question: "What types of messages can I send through the Shopify WhatsApp automation?",
                        answer: "You can use them to reduce cart abandonment, notify order confirmations, shipment updates, product updates. You can run promotions, announce new products and services, share images of your products, or offer better customer service.",
                    },
                    {
                        question: "How does the abandoned cart with WhatsApp reminders work?",
                        answer: "When a customer adds items to their shopping cart on an online store but leaves without completing the purchase, Gallabox triggers an automated WhatsApp reminder.",
                    },
                    {
                        question: "How do I set up the Abandoned Cart with WhatsApp Reminders on Gallabox?",
                        answer: (
                            <span>
                                Install Gallabox from&nbsp;
                                <a
                                    className="text-inherit hover:underline underline inline-block text-blue-color"
                                    href=" https://apps.shopify.com/gallabox"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Shopify Marketplace
                                </a>
                                . Under the workflows enable cart abandonment. Choose the WhatsApp template you want to trigger. Tadaa you have enabled the cart abandonment! &nbsp;
                            </span>
                        ),
                    },
                ]}
            />
        </NewLayout>
    );
};

export default shopifyLanding;
